import axios from "axios";
import dayjs from "../utils/dayjsConfig";
import { attachSentryResponseInterceptor } from "./sentryAxiosInterceptor";

//TYPES
import {
  AuthData,
  CreateInvestmentFundsOperationParams,
  CreateOperationParams,
  OrderType,
} from "../types/types";
import { PeriodTab } from "../pages/ActivityPage/ui/ActivityTable/types/types";
import {
  BymaLimit,
  FeesAmount,
  FundPositions,
  InvestmentProfileInfo,
  OrderData,
  OrdersPositions,
  UserCanOperateResponse,
} from "../pages/MarketPage/ui/OperateModal/types";
import {
  Operation,
  SettledPeriod,
  State,
  OrderType as Type,
} from "../pages/ActivityPage/ui/OrdersTable/types/types";
import {
  PRIVACY_POLICY_VERSION,
  TERMS_AND_CONDITIONS_VERSION,
} from "../store/slices/termsAndConditionsSlice";

//AXIOS INSTANCE

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_OPEX_SVC_URL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    const localAuth = localStorage.getItem("allaria-auth");

    const parsedLocalAuth: AuthData | null =
      (localAuth && JSON.parse(localAuth)) ?? null;

    const token = parsedLocalAuth?.access_token;

    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);
attachSentryResponseInterceptor(axiosInstance);

const ORDER_TYPE_PATHS: Record<OrderType, string> = {
  MARKET: "market",
  LIMIT: "limit",
  STOP_LIMIT: "stop-limit",
} as const;

export const createOperation = async ({
  data,
  type,
}: CreateOperationParams): Promise<{ order_id: string }> => {
  if (!Object.keys(ORDER_TYPE_PATHS).includes(type)) {
    throw new Error(`Invalid order type: ${type}`);
  }

  const path = ORDER_TYPE_PATHS[type];
  const response = await axiosInstance.post(
    `/broker/orders/byma/${path}`,
    data,
  );
  return response.data;
};

export const createInvestmentFundsOperation = async ({
  data,
}: CreateInvestmentFundsOperationParams): Promise<{
  mutual_fund_id: string;
}> => {
  const response = await axiosInstance.post(`/broker/funds/send`, data);
  return response.data;
};

type OperationsResponse = Promise<{
  data: Operation[];
  total: number;
  page: number;
  size: number;
}>;

export type Currency = "ARS" | "DOLLAR_MEP" | "DOLLAR_CCL";

export const getOperations = async ({
  accountId,
  period,
  page,
  size,
  dateOption,
  fromDate,
  toDate,
  state,
  settledPeriod,
  currency,
  type,
}: {
  accountId: number | string;
  period: PeriodTab;
  page: number;
  size: number;
  dateOption: "0" | "7" | "15" | "30" | "CUSTOM" | null;
  fromDate?: string; // YYYY-MM-DD
  toDate?: string; // YYYY-MM-DD
  state: "ALL" | State[] | null;
  settledPeriod: "ALL" | SettledPeriod[] | null;
  currency?: "ALL" | Currency[] | null;
  type: "ALL" | Type[] | null;
}) => {
  const createUrlWithQueryParams = () => {
    const baseUrl = import.meta.env.VITE_OPEX_SVC_URL;

    const url = new URL(`/broker/operations/${accountId}`, baseUrl);

    const today = dayjs().format("YYYY-MM-DD");
    const yesterday = dayjs().subtract(1, "day").format("YYYY-MM-DD");

    const baseParams = {
      from: "",
      to: "",
      page,
      size,
    };

    if (period === "TODAY") {
      baseParams["from"] = today;
      baseParams["to"] = today;
    } else {
      baseParams["to"] = yesterday;

      switch (dateOption) {
        case "7":
          baseParams["from"] = dayjs().subtract(7, "days").format("YYYY-MM-DD");
          break;
        case "15":
          baseParams["from"] = dayjs()
            .subtract(15, "days")
            .format("YYYY-MM-DD");
          break;
        case "30":
          baseParams["from"] = dayjs()
            .subtract(30, "days")
            .format("YYYY-MM-DD");
          break;
        case "CUSTOM":
          baseParams["from"] = fromDate || "";
          baseParams["to"] = toDate || "";
          break;
        default:
          break;
      }
    }

    Object.entries(baseParams).forEach(([key, value]) => {
      if (value && value !== "ALL") {
        url.searchParams.append(key, String(value));
      }
    });

    const arrayParams: {
      [key: string]:
        | State[]
        | SettledPeriod[]
        | Currency[]
        | Type[]
        | "ALL"
        | undefined
        | null;
    } = {
      state,
      settledPeriod,
      currency,
      type,
    };

    Object.entries(arrayParams).forEach(([key, values]) => {
      if (Array.isArray(values)) {
        values?.forEach((value) => {
          url.searchParams.append(key, value);
        });
      }
    });

    return url.toString();
  };

  const url = createUrlWithQueryParams();

  const response = await axiosInstance.get(url);
  return response.data as OperationsResponse;
};

export const getFeesAmounts = async ({
  accountId,
  symbol,
}: {
  accountId: number | string;
  symbol: string;
}) => {
  const response = await axiosInstance.get(
    `/broker/fees/${accountId}/${symbol}`,
  );
  return response.data as FeesAmount;
};

type CancelResponse = Promise<{
  status_code: string;
  message: string;
}>;

export const cancelSingleOrder = async (orderId: number | string | null) => {
  const response = await axiosInstance.delete(
    `/broker/orders/cancel/${orderId}`,
    {
      data: {},
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    },
  );
  return response.data as CancelResponse;
};

type HolidaysResponse = Promise<{
  is_holiday_today: boolean;
  next_holiday_date: string; // YYYY-MM-DD
  next_business_day: string; // YYYY-MM-DD
  market_can_operate_t1_today: boolean;
  following_business_day: string; // YYYY-MM-DD
}>;

export const getHolidays = async () => {
  const response = await axiosInstance.get(`/broker/holidays/assessment`);
  return response.data as HolidaysResponse;

  // return {
  //   is_holiday_today: true,
  //   next_business_day: "2025-11-25",
  //   market_can_operate_t1_today: true,
  // };
};

export const getBymaLimits = async () => {
  const response = await axiosInstance.get(`/broker/byma/data`);
  return response.data as BymaLimit[];
};

export const getMEPQuote = async ({ accountId }: { accountId: string }) => {
  const response = await axiosInstance.get(
    `/broker/operations/mep/quote/${accountId}`,
  );
  return response.data;
};

export const getAffidavitStatus = async ({
  accountId,
}: {
  accountId: string;
}) => {
  const response = await axiosInstance.get(
    `/broker/document/affidavit/account/${accountId}/today`,
  );
  return response.data;
};

export const getDocumentIsSigned = async ({
  accountId,
  documentType,
  description,
}: {
  accountId: string;
  documentType: "AFFIDAVIT" | "MUTUAL_FUNDS";
  description: string; // MUTUAL_FUNDS: bloombergCode, AFFIDAVIT: "BUY" | "SELL"
}) => {
  const response = await axiosInstance.get(`/broker/document/signed`, {
    params: {
      account_id: accountId,
      document_type: documentType,
      description,
    },
  });
  return response.data;
};

export const getFundsPositions = async ({
  accountId,
}: {
  accountId: string;
}) => {
  const response = await axiosInstance.get(
    `/broker/operations/position/funds/${accountId}`,
  );
  return response.data as FundPositions;
};

export const notifySignDocument = async ({
  accountId,
  documentType,
  description,
}: {
  accountId: string;
  documentType: "AFFIDAVIT" | "MUTUAL_FUNDS";
  description: string; // MUTUAL_FUNDS: bloombergCode, AFFIDAVIT: "BUY" | "SELL"
}) => {
  const response = await axiosInstance.post(`/broker/document/sign`, {
    account_id: accountId,
    document_type: documentType,
    description,
  });
  return response.data;
};

export const signTermsAndConditions = async ({
  accountId,
}: {
  accountId: string;
}) => {
  const response = await axiosInstance.post(`/broker/document/sign`, {
    account_id: accountId,
    document_type: "TERMS_AND_CONDITIONS",
    version: TERMS_AND_CONDITIONS_VERSION,
    privacy_policy: PRIVACY_POLICY_VERSION,
  });
  return response.data;
};

export const createMepOperation = async ({
  side,
  accountId,
  data,
}: {
  side: string;
  accountId: string;
  data: {
    amount: number;
    estimated_price: number;
  };
}) => {
  const response = await axiosInstance.post(
    `/broker/operations/mep/${accountId}/${side}`,
    data,
  );
  return response.data;
};

export const getSymbolsPositions = async ({
  accountId,
}: {
  accountId: string;
}) => {
  const response = await axiosInstance.get(
    `/broker/operations/position/orders/${accountId}`,
  );
  return response.data as OrdersPositions;
};

export const getMepOperationData = async ({ id }: { id: string }) => {
  const response = await axiosInstance.get(`/operations/usd-mep/${id}`);
  return response.data;
};

export const isResident = async ({ accountId }: { accountId: string }) => {
  const response = await axiosInstance.get(
    `/operations/usd-mep/is-resident/${accountId}`,
  );
  return response.data;
};

export const getOrderById = async ({ orderId }: { orderId: string }) => {
  const response = await axiosInstance.get(`/broker/orders/id/${orderId}`);
  return response.data as OrderData;
};

export const getOrderFundById = async ({ orderId }: { orderId: string }) => {
  const response = await axiosInstance.get(`/broker/funds/id/${orderId}`);
  return response.data;
};

export const getAccountInvestmentProfile = async ({
  accountId,
}: {
  accountId: Number;
}) => {
  const response = await axiosInstance.get(
    `/broker/investment-profile/${accountId}`,
  );
  return response.data as InvestmentProfileInfo;
};

export const updateAccountInvestmentProfile = async ({
  accountId,
  riskLevel,
}: {
  accountId: Number;
  riskLevel: string;
}) => {
  const response = await axiosInstance.put(
    "/broker/investment-profile/update/risk-level",
    { account_id: accountId, risk_level: riskLevel },
  );
  return response.data;
};

export const editOrder = async ({
  orderId,
  data,
}: {
  orderId: string;
  data: {
    quantity?: number;
    limit_price?: number;
    amount?: number;
  };
}) => {
  const response = await axiosInstance.put(
    `/broker/orders/edit/${orderId}`,
    data,
  );
  return response.data;
};

export const userCanOperate = async ({ accountId }: { accountId: string }) => {
  const response = await axiosInstance.get(
    `/broker/operations/is-allowed/${accountId}`,
  );
  return response.data as UserCanOperateResponse;
};
